import { useMemo, useState } from "react";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useBayHireQueuePaged } from "@features/bay-hire/hooks/useBayHireQueries";
import type { BayHireQueueRow } from "@features/bay-hire/types";
import {
  bayHirePaymentTypeLabel,
  bayHirePreferenceLabel,
  bayHireStatusLabel,
  toBayHireSelectOptions,
} from "@features/bay-hire/utils/bayHireLabels";
import { applyDateRangeContainmentFilter } from "@features/bay-hire/utils/formatBayHireDate";
import type {
  BayHireRequestStatus,
  BayHireSearchField,
  BayHireSearchFilter,
} from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

// DFT-0027 (Slice E4): Figma p13/p21 default "Rows per page 10" (was 50),
// matching the vendor list's DEFAULT_PAGE_SIZE.
const DEFAULT_QUEUE_PAGE_SIZE = 10;

interface UseBayHireOperationsQueueResult {
  rows: BayHireQueueRow[];
  isLoading: boolean;
  isError: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;
  filters: FilterItem[];
  availableFields: Record<string, FilterFieldDefinition>;
  handleApplyFilters: (filters: FilterItem[]) => void;
  handleClearFilters: () => void;
}

// DFT-0040: the Ongoing queue's REQUESTED and PAYED requests are matched
// together in one query - a request awaiting payment verification is
// discoverable through the queue listing itself, not only actionable by
// direct ID (the Task Management join already handles either task purpose
// per row regardless of which status brought it into the result set).
//
// Megaplan (2026-08-05, item 6): widened from [REQUESTED, PAYED] to every
// status EXCEPT WAITING_LIST - Operations previously had no way to list
// PRE_APPROVED/APPROVED/REJECTED/CANCELLED requests at all (only reachable
// one at a time, by direct id, via the detail page). No backend query change
// was needed for this - see bff-bay-hire-service's
// BayHireQueueSearchPaged.statuses, widened the same day to accept every
// status.
//
// Megaplan follow-up (2026-08-05, Operations 3-tab restructure): the above
// widening put resolved requests (APPROVED/REJECTED/CANCELLED) in the SAME
// tab as genuinely active ones, under a subtitle that says "awaiting
// Operations review" - wrong-tab, not duplicate-tab, but still wrong. Split
// into an exact 3-way partition of all 7 statuses so every request lives in
// exactly one tab: Ongoing keeps only the statuses that still need Ops
// attention (shrunk back from 6 to 3), WAITING_LIST is unchanged, and the
// 3 resolved/terminal statuses move to a new HISTORY tab.
const STATUSES_BY_VIEW: Record<"REQUESTED" | "WAITING_LIST" | "HISTORY", BayHireRequestStatus[]> = {
  REQUESTED: ["REQUESTED", "PRE_APPROVED", "PAYED"],
  WAITING_LIST: ["WAITING_LIST"],
  HISTORY: ["APPROVED", "REJECTED", "CANCELLED"],
};

// DFT-0027/DFT-0069 (Slice G): translates the Filters panel's generic FilterItem[] directly
// into the generic BayHireSearchFilter[] the backend now expects - same mapping as the vendor
// list's buildFiltersFromUI (useBayHireRequestList.tsx), minus `status`, which is handled
// separately below because it must be intersected with the tab's own allowed status set
// rather than sent verbatim. FilterItems sharing the same `field` are merged into one entry's
// `values` array (OR semantics), mirroring vessel-call-management-service/PortCalls.tsx's own
// buildFilterPayload.
const pushFilter = (filters: BayHireSearchFilter[], field: BayHireSearchField, value: string) => {
  const existing = filters.find((f) => f.field === field);
  if (existing) {
    if (!existing.values.includes(value)) existing.values.push(value);
  } else {
    filters.push({ field, values: [value] });
  }
};

const buildFiltersFromUI = (items: FilterItem[]): BayHireSearchFilter[] => {
  const filters: BayHireSearchFilter[] = [];
  for (const item of items) {
    if (!item.field || item.value === "" || item.value == null) continue;
    switch (item.field) {
      case "paymentType":
        pushFilter(filters, "paymentType", item.value as string);
        break;
      case "bayPreference":
        pushFilter(filters, "bayPreference", item.value as string);
        break;
      case "dateSubmitted": {
        const range = item.value as { from?: string; to?: string };
        if (range.from) pushFilter(filters, "createdAtFrom", range.from);
        if (range.to) pushFilter(filters, "createdAtTo", range.to);
        break;
      }
      // DFT-0063: same mapping as the vendor list's buildFiltersFromUI (useBayHireRequestList.tsx).
      case "requestNo": {
        const value = item.value as string;
        const parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed)) pushFilter(filters, "requestNo", String(parsed));
        break;
      }
      case "responsiblePersonName":
        pushFilter(filters, "responsiblePersonName", item.value as string);
        break;
      // DFT-0075: "Date" is a containment filter - see useBayHireRequestList.tsx's identical
      // dateRange case and applyDateRangeContainmentFilter's own doc comment for the full
      // rationale.
      case "dateRange":
        applyDateRangeContainmentFilter(
          filters,
          pushFilter,
          item.value as { from?: string; to?: string },
        );
        break;
      case "numberOfBays": {
        const value = item.value as string;
        const parsed = Number.parseInt(value, 10);
        if (!Number.isNaN(parsed)) pushFilter(filters, "numberOfBays", String(parsed));
        break;
      }
      default:
        break;
    }
  }
  return filters;
};

// DFT-0027 (Slice G): a user-chosen status may only NARROW the tab's own
// status set, never widen it - the Waiting list tab must never be able to
// show REQUESTED rows, and the Ongoing tab must never show WAITING_LIST
// rows. Implemented as an intersection: an out-of-set (or absent) choice
// falls back to the full allowed set for that tab.
//
// Bug fix (found live, 2026-08-31): used to read only `items.find(...)`'s first "status"
// FilterItem - adding a SECOND status filter row (the Filters panel lets a field be added more
// than once, exactly how every other field's OR-merge is supposed to work; see
// useBayHireRequestList.tsx's own `pushFilter`) was silently discarded entirely, so only the
// first-chosen status ever took effect. Now collects every chosen status across all "status"
// FilterItems and intersects the union with the tab's allowed set, giving status the same
// multi-row OR semantics as every other field.
const resolveStatuses = (
  allowed: BayHireRequestStatus[],
  items: FilterItem[],
): BayHireRequestStatus[] => {
  const chosen = items
    .filter(
      (item) => item.field === "status" && typeof item.value === "string" && item.value !== "",
    )
    .map((item) => item.value as string);
  if (chosen.length === 0) return allowed;
  const intersection = allowed.filter((status) => chosen.includes(status));
  return intersection.length ? intersection : allowed;
};

/**
 * DFT-0009/DFT-0014/DFT-0015 Operations queue read/pagination data.
 *
 * @param status "REQUESTED" for the Ongoing queue (default, DFT-0040:
 * queries REQUESTED + PAYED together), "WAITING_LIST" for the Waiting List
 * view (DFT-0015), "HISTORY" for the resolved/terminal queue (Megaplan
 * 2026-08-05 3-tab restructure).
 *
 * DFT-0015: real offset pagination (page/pageSize, localStorage-persisted per
 * status, matching Gatepass/Security Clearance/Licensing/Vessel Call/Vessel
 * Management convention) - previously this hardcoded page 1 because the backend
 * only supported a top-N limit (see DFT-0026, which flagged this as a deferred
 * prerequisite the backend now satisfies).
 *
 * DFT-0027 (Slice D): the lifecycle actions (claim/unclaim/verify-payment and
 * the pre-approve/reject/cancel/reject-payment/add-to-waiting-list dialog row
 * state) that this hook previously owned moved to the routed Request Detail
 * page (useBayHireDetailActions). Both queue components now consume only the
 * read/pagination state below, so this hook is read-only.
 *
 * DFT-0027 (Slice G): plus the Filters panel state (Figma p13/p21), mirroring
 * the vendor list (useBayHireRequestList). Filters are persisted per tab
 * (same per-status localStorage key convention as page/pageSize), so the
 * Ongoing, Waiting list, and History tabs each keep independent filter state.
 */
export const useBayHireOperationsQueue = (
  status: "REQUESTED" | "WAITING_LIST" | "HISTORY" = "REQUESTED",
): UseBayHireOperationsQueueResult => {
  const { t } = useTranslation();
  const pageKey = `bay-hire-queue-${status.toLowerCase()}-page`;
  const pageSizeKey = `bay-hire-queue-${status.toLowerCase()}-page-size`;
  const filtersKey = `bay-hire-queue-${status.toLowerCase()}-filters`;

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(pageKey);
    return saved ? Number.parseInt(saved, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(pageSizeKey);
    return saved ? Number.parseInt(saved, 10) : DEFAULT_QUEUE_PAGE_SIZE;
  });
  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(filtersKey);
    return saved ? JSON.parse(saved) : [];
  });

  const statuses = useMemo(
    () => resolveStatuses(STATUSES_BY_VIEW[status], filters),
    [status, filters],
  );
  // DFT-0069: the resolved status set folds into the generic filters[] as its own `status`
  // entry, alongside every other Filters panel field - no separate top-level parameter.
  const queueFilters = useMemo<BayHireSearchFilter[]>(
    () => [{ field: "status", values: statuses }, ...buildFiltersFromUI(filters)],
    [statuses, filters],
  );

  // DFT-0027 (Slice G): the Waiting list tab deliberately offers no `status`
  // field - it only ever contains WAITING_LIST rows, so a status select there
  // could only be a no-op (or, if it widened the set, a correctness bug). The
  // Ongoing and History tabs each offer exactly the statuses they can show
  // (see STATUSES_BY_VIEW's own comment - Megaplan 2026-08-05 3-tab
  // restructure).
  // DFT-0063: ordered to match the grid's own column order (ID | Name of Port company
  // responsible | No of bays | Request Bay | Payment type | Date From/Date To | Date
  // submitted | Status) - see BayHireOperationsQueue.tsx's headers array.
  const availableFields = useMemo<Record<string, FilterFieldDefinition>>(() => {
    const fields: Record<string, FilterFieldDefinition> = {
      requestNo: {
        label: t("bay-hire.table.id"),
        type: "integer" as const,
        minLength: 1,
      },
      responsiblePersonName: {
        label: t("bay-hire.table.responsiblePerson"),
        type: "text" as const,
        minLength: 3,
      },
      numberOfBays: {
        label: t("bay-hire.table.bayNo"),
        type: "integer" as const,
        minLength: 1,
      },
      bayPreference: {
        label: t("bay-hire.table.requestBay"),
        type: "select" as const,
        options: toBayHireSelectOptions(["INSIDE_PORT", "OUTSIDE_PORT"], bayHirePreferenceLabel, t),
      },
      // DFT-0044: humanized options, matching the detail page (FE-43) - previously showed the raw
      // enum ("MONTHLY"/"INSIDE_PORT") in the Filters dropdown.
      paymentType: {
        label: t("bay-hire.table.paymentType"),
        type: "select" as const,
        options: toBayHireSelectOptions(
          ["MONTHLY", "YEARLY", "EXEMPTED"],
          bayHirePaymentTypeLabel,
          t,
        ),
      },
      // DFT-0075: "Date" is a containment filter - see useBayHireRequestList.tsx's dateRange
      // case for the rationale.
      dateRange: {
        label: t("bay-hire.filters.dateRange"),
        type: "dateFromToWithoutTime" as const,
      },
      // DFT-0075: full offset-aware date-time - see useBayHireRequestList.tsx's identical
      // change for the rationale.
      dateSubmitted: {
        label: t("bay-hire.table.dateSubmitted"),
        type: "dateFromTo" as const,
      },
    };
    if (status === "REQUESTED" || status === "HISTORY") {
      return {
        ...fields,
        status: {
          label: t("bay-hire.table.status"),
          type: "select" as const,
          options: toBayHireSelectOptions(STATUSES_BY_VIEW[status], bayHireStatusLabel, t),
        },
      };
    }
    return fields;
  }, [status, t]);

  const {
    data = { rows: [], totalCount: 0 },
    isLoading,
    isError,
  } = useBayHireQueuePaged(page - 1, pageSize, queueFilters);

  const onPageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
    localStorage.setItem(pageKey, String(newPage));
    localStorage.setItem(pageSizeKey, String(newPageSize));
  };

  const handleApplyFilters = (newFilters: FilterItem[]) => {
    setFilters(newFilters);
    localStorage.setItem(filtersKey, JSON.stringify(newFilters));
    setPage(1);
    localStorage.setItem(pageKey, "1");
  };

  const handleClearFilters = () => {
    setFilters([]);
    localStorage.removeItem(filtersKey);
    setPage(1);
    localStorage.setItem(pageKey, "1");
  };

  return {
    rows: data.rows,
    isLoading,
    isError,
    page,
    pageSize,
    totalCount: data.totalCount,
    onPageChange,
    filters,
    availableFields,
    handleApplyFilters,
    handleClearFilters,
  };
};
